import {
  AgentPipelineModel,
  AllDepartmentsPipelineModel,
  AppointmentModel,
  CreateAppointmentModel,
  DashboardPipelineModel,
  DepartmentPipelineModel,
  PredictionModel,
  PredictionType,
  SalesHierarchyModel,
} from "@/models/pipeline";
import apiClient, { getPhoenixToken } from "./apiClient";
import { formatPipelineData } from "@/helpers/pipeline";
import { isOfflineMode } from "@/helpers/offlineMode";
import {
  mockAgentPipelineData,
  mockAllDepartmentsPipelineData,
  mockDepartmentPipelineData,
  mockDirectorOverviewData,
  mockSalesHierarchy,
} from "./mockData";

const PIPELINE_BASE_PATH = "/pipeline";

const getAgentPipeline = async (agentId: number, abortSignal?: AbortSignal): Promise<AgentPipelineModel> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockAgentPipelineData;
  }

  const response = await apiClient.get(`${PIPELINE_BASE_PATH}/agent/${agentId}`, {
    signal: abortSignal,
  });
  return formatPipelineData(response.data) as AgentPipelineModel;
};

const getDepartmentPipeline = async (
  departmentId: string,
  abortSignal?: AbortSignal
): Promise<DepartmentPipelineModel> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockDepartmentPipelineData;
  }

  const response = await apiClient.get(`${PIPELINE_BASE_PATH}/department/${departmentId}`, { signal: abortSignal });
  return formatPipelineData(response.data) as DepartmentPipelineModel;
};

const getAllDepartmentsPipeline = async (abortSignal?: AbortSignal): Promise<AllDepartmentsPipelineModel> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const data = mockAllDepartmentsPipelineData;
    const sortedDepartmentGroups = data.statisticsGroups
      .filter((group) => group.departmentGroupName)
      .sort((a, b) => a.departmentGroupSortOrder - b.departmentGroupSortOrder);
    const sortedStatistics = sortedDepartmentGroups.flatMap((group) =>
      group.statistics.sort((a, b) =>
        a.departmentName.localeCompare(b.departmentName, undefined, {
          numeric: true,
        })
      )
    );
    return {
      ...data,
      statisticsGroups: sortedDepartmentGroups,
      statistics: sortedStatistics,
    };
  }

  const response = await apiClient.get(`${PIPELINE_BASE_PATH}/all`, {
    signal: abortSignal,
  });

  const data = response.data as AllDepartmentsPipelineModel;

  const sortedDepartmentGroups = data.statisticsGroups
    .filter((group) => group.departmentGroupName)
    .sort((a, b) => a.departmentGroupSortOrder - b.departmentGroupSortOrder);

  const sortedStatistics = sortedDepartmentGroups.flatMap((group) =>
    group.statistics.sort((a, b) =>
      a.departmentName.localeCompare(b.departmentName, undefined, {
        numeric: true,
      })
    )
  );

  return {
    ...data,
    statisticsGroups: sortedDepartmentGroups,
    statistics: sortedStatistics,
  };
};

const getPinnedApplications = async (agentId: string | number | null): Promise<string[]> => {
  if (!agentId || agentId === -1) {
    return [];
  }

  if (isOfflineMode()) {
    return [];
  }

  const response = await apiClient.get(`/users/${agentId}/pinned-applications-by-portal`);
  return response.data ?? [];
};

const savePinnedApplications = async (agentId: string | number | null, pinnedApps: string[]): Promise<void> => {
  if (!agentId || agentId === -1) {
    return;
  }

  if (isOfflineMode()) {
    return;
  }

  await apiClient.post(`/users/${agentId}/pinned-applications-by-portal`, pinnedApps);
};

const makePrediction = async (applicationId: number, predictionType: PredictionType): Promise<void> => {
  if (isOfflineMode()) {
    return;
  }

  await apiClient.post(`${PIPELINE_BASE_PATH}/prediction`, {
    applicationId,
    predictionType,
  });
};

const removePrediction = async (applicationId: number, predictionType: PredictionType): Promise<void> => {
  if (isOfflineMode()) {
    return;
  }

  const requestBody: PredictionModel = {
    applicationId,
    predictionType,
  };

  await apiClient.delete(`${PIPELINE_BASE_PATH}/prediction`, {
    data: requestBody,
  });
};

const createAppointment = async (appointment: CreateAppointmentModel): Promise<void> => {
  if (isOfflineMode()) {
    return;
  }

  await apiClient.post(`${PIPELINE_BASE_PATH}/calendar`, appointment);
};

const getAppointments = async (
  agentId: number,
  from: Date,
  to: Date,
  abortSignal?: AbortSignal
): Promise<AppointmentModel[]> => {
  if (isOfflineMode()) {
    return [];
  }

  const response = await apiClient.get(`${PIPELINE_BASE_PATH}/calendar`, {
    params: {
      agentId,
      from: from.toISOString(),
      to: to.toISOString(),
    },
    signal: abortSignal,
  });

  return response.data as AppointmentModel[];
};

const getDirectorOverview = async (
  selectedDate: string,
  abortSignal?: AbortSignal
): Promise<DashboardPipelineModel> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockDirectorOverviewData;
  }

  const response = await apiClient.get(`${PIPELINE_BASE_PATH}/overview/allhob`, {
    params: { selectedDate },
    signal: abortSignal,
  });

  return response.data as DashboardPipelineModel;
};

const getHobOverview = async (
  headOfSaleUserId: number,
  selectedDate: string,
  abortSignal?: AbortSignal
): Promise<DashboardPipelineModel> => {
  const response = await apiClient.get(`${PIPELINE_BASE_PATH}/overview/hob`, {
    params: { selectedDate, headOfSaleUserId },
    signal: abortSignal,
  });
  return response.data as DashboardPipelineModel;
};

const getOsmOverview = async (
  salesManagerUserId: number,
  selectedDate: string,
  abortSignal?: AbortSignal
): Promise<DashboardPipelineModel> => {
  const response = await apiClient.get(`${PIPELINE_BASE_PATH}/overview/salesteams`, {
    params: { selectedDate, salesManagerUserId },
    signal: abortSignal,
  });
  return response.data as DashboardPipelineModel;
};

const getTeamOverview = async (
  departmentId: string,
  selectedDate: string,
  abortSignal?: AbortSignal
): Promise<DashboardPipelineModel> => {
  const response = await apiClient.get(`${PIPELINE_BASE_PATH}/overview/team`, {
    params: { departmentId, selectedDate },
    signal: abortSignal,
  });
  return response.data as DashboardPipelineModel;
};

const getSalesHierarchy = async (abortSignal?: AbortSignal): Promise<SalesHierarchyModel[]> => {
  if (isOfflineMode()) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return mockSalesHierarchy;
  }

  const response = await apiClient.get(`/users/sales-hierarchy`, {
    signal: abortSignal,
  });

  return response.data as SalesHierarchyModel[];
};

const isReady = (): boolean => {
  if (isOfflineMode()) {
    return true;
  }
  return !!getPhoenixToken();
};

export const pipelineService = {
  getAgentPipeline,
  getDepartmentPipeline,
  getAllDepartmentsPipeline,
  getDirectorOverview,
  getHobOverview,
  getOsmOverview,
  getTeamOverview,
  getSalesHierarchy,
  getPinnedApplications,
  savePinnedApplications,
  makePrediction,
  removePrediction,
  createAppointment,
  getAppointments,
  isReady,
};
